/**
 * quickViewGuard: a Quick View leaves no trace on the server.
 *
 * A Quick View (`project_quick_*`) is a throwaway viewer for files on the
 * person's own disk. Its project record lives in the TAB (utils/storage keeps
 * it in sessionStorage), and nothing about it is sent anywhere: no objects,
 * regions or OCR, no recent documents, no previews, no activity, no drafts.
 * Its work stays in memory until the person saves a file to disk or leaves.
 *
 * `apiFetch` asks `quickViewBlocked` before every request and answers a
 * blocked one itself, without touching the network. The only calls a Quick
 * View still makes are the ones that work on bytes and hand the result back,
 * storing nothing: the PDF bake and the transient upload behind it, Ask
 * pidly, the detector, and SharePoint / library reads for a Quick View opened
 * from pidly Documents.
 *
 * MIRROR of backend/utils/systemProject.js (`QUICK_PROCESSING`,
 * `quickViewGuard`), which refuses the same requests on the server. Change
 * one, change the other: backend/utils/systemProject.eval.js reads this file
 * and fails if the two allow different calls.
 */
import { QUICK_PREFIX } from './boardWorkspace';

const QUICK_RE = new RegExp(`${QUICK_PREFIX}[A-Za-z0-9_-]+`);

export const QUICK_PROCESSING = Object.freeze([
  '/api/pdf/', '/api/upload', '/api/ai/', '/api/detector', '/api/sharepoint',
  '/api/files/', '/api/client-errors',
]);

/** Is this project id a Quick View? */
export const isQuickViewId = (id) => typeof id === 'string' && id.startsWith(QUICK_PREFIX);

/** Is the page the person is on a Quick View? */
export function inQuickView() {
  try { return /\/project\/project_quick_/.test(window.location.pathname); } catch { return false; }
}

function pathOf(url) {
  try {
    const u = new URL(url, 'http://local');
    return { path: u.pathname, full: u.pathname + u.search };
  } catch { return { path: String(url || ''), full: String(url || '') }; }
}

/**
 * Does a request about a Quick View have to stay in the browser? Only WRITES
 * are kept back here: a read goes to the server, which finds nothing for a
 * Quick View and says so with a 404 every reader already handles (answering
 * reads here with a made-up body would hand callers a shape they do not
 * expect).
 */
export function quickViewBlocked(url, options = {}, headers = {}) {
  const method = String(options.method || 'GET').toUpperCase();
  if (method === 'GET' || method === 'HEAD') return false;
  const { path, full } = pathOf(url);
  let named = QUICK_RE.test(full);
  if (!named) {
    const h = headers['X-Pidly-Project-Id'] || headers['x-pidly-project-id'];
    if (isQuickViewId(h)) named = true;
  }
  if (!named && typeof options.body === 'string' && options.body.includes(QUICK_PREFIX)) {
    // Only a body that NAMES a Quick View as its project; a quick id inside
    // free text (a chat message, a description) is not a request about one.
    named = /"projectId"\s*:\s*"project_quick_|"project"\s*:\s*\{\s*"id"\s*:\s*"project_quick_/.test(options.body);
  }
  if (!named) return false;
  return !QUICK_PROCESSING.some(prefix => path.startsWith(prefix));
}

/** The answer a blocked request gets: acknowledged, and nothing to read. */
export function quickViewResponse() {
  return new Response(JSON.stringify({ success: true, quickView: true }), {
    status: 200,
    headers: { 'Content-Type': 'application/json' },
  });
}
